/**
 * PlatformConsentScreen — the warm, under-13 LIMITED-MODE / parent-invite
 * overlay raised when an under-13 (no `platform_minor` consent) trips an
 * open-section egress (the tutor, Learn, Prep, the advisors).
 *
 * WARM, NEVER RED (the student-state rule): an under-13 in limited mode has
 * done nothing wrong — this is an "ask a grown-up to switch this on" moment, so
 * the whole surface uses the amber "encourage" palette. Red is reserved for
 * actual system errors (the link-verify failure inline message). There is no
 * dead end: the child can keep using the open parts that don't egress, and the
 * screen lays out the exact two steps a parent takes to unlock the rest.
 *
 * REUSES THE GUARDIAN-LINK UI: a child can't grant their own consent (the whole
 * point of the gate). The unlock is a GUARDIAN action on the guardian's own
 * account (Guardian access → link → grant the platform permission). This screen
 * gives the child the relay path: they enter a one-time code their parent
 * generated (the SAME verify flow admissions ships — `verifyLink`), so the link
 * gets established; the parent then grants `platform_minor` from THEIR device.
 *
 * The overlay is DISMISSIBLE (the child keeps navigating). It is the shared
 * affordance over the top; each egress surface also rejects its own call so it
 * can render an inline limited state if it wants.
 *
 * A REAL MODAL via the shared <Modal> primitive: labelled + described, focus
 * trapped, Escape and a scrim click dismiss, body scroll held. Initial focus
 * lands on the heading (a non-interactive but focusable anchor) so a
 * screen-reader user hears the limited-mode explanation first. It used to
 * hand-roll all of that with its own document keydown listener, so when it
 * opened over another dialog (the Progress walkthrough whose tutor call was
 * refused, Settings' state-standards picker) one Escape closed both. On the
 * modal stack only the dialog on top acts on a key.
 *
 * FOCUS RESTORE (WCAG 2.4.3): like the DOB modal, this is interceptor-driven
 * (a 403 raises it, no trigger element), so on close <Modal> returns focus to
 * the opener if there is one, into the dialog still open beneath it if not,
 * otherwise to the page's `#main-content` skip-nav target — never leaving a
 * keyboard/SR child stranded at `<body>` after Escape or "Keep exploring".
 *
 * WHILE THE CODE IS BEING CHECKED (components/ui/busyForm.js): the overlay
 * can't be left (Escape, the scrim, "Keep exploring" and the Guardian access
 * link are all refused), so the answer always has somewhere to show; a one-time
 * code linked behind a closed overlay is spent, and the child who tries it
 * again is told it didn't work. The code field and Confirm are never natively
 * disabled (a browser drops focus from a control the moment it is): the field
 * turns read-only, Confirm aria-disabled, and whichever started the check
 * keeps focus. A refused code puts focus in the field; a linked one removes
 * the form, so focus lands on the "Linked!" line that replaces it.
 *
 * Props:
 *   onClose (fn) — hide the overlay (the child taps "Keep exploring").
 *   currentUserId (string|null) — UX input only; not required for the flow.
 */
import { useRef, useState } from 'react';
import { Link } from 'react-router';
// Direct paths, not the components/ui barrel: this screen is in the EAGER entry
// graph (App.js → AgeConsentGate), where AgeGateModal's rule applies.
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import PendingStatus from '../components/ui/PendingStatus';
import { busyButton, busyForm, useRefusedFieldFocus } from '../components/ui/busyForm';
import useGateFocus from '../hooks/useGateFocus';
import { isLinkCodeRefused, verifyLink } from '../admissions/admissionsApi';
import { friendlyError } from './coppaApi';
import './coppa.css';

const LINKED_ID = 'coppa-pc-linked';
const TOKEN_ERROR_ID = 'coppa-pc-token-error';

export default function PlatformConsentScreen({ onClose }) {
  const headingRef = useRef(null);
  const tokenRef = useRef(null);
  const focusRefused = useRefusedFieldFocus(tokenRef);

  const [token, setToken] = useState('');
  const [verifying, setVerifying] = useState(false);
  // { message, codeRefused } | null
  const [verifyError, setVerifyError] = useState(null);
  const [verified, setVerified] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    setVerifying(true);
    setVerifyError(null);
    try {
      await verifyLink(token.trim());
      setVerified(true);
      setToken('');
    } catch (err) {
      const codeRefused = isLinkCodeRefused(err);
      setVerifyError({
        // The server's own words when it sent any. Without them, only a
        // refusal may say the code didn't work: after no answer (the deadline
        // passed, or the request or its answer was lost) the code may be
        // fine, or already linked (admissionsApi.verifyLink), and "ask for a
        // fresh one" would send the child off for a code they don't need.
        message: friendlyError(
          err,
          codeRefused
            ? 'That code didn’t work. Ask your grown-up to send a fresh one.'
            : 'We couldn’t check that code just now. Please try again.',
        ),
        codeRefused,
      });
      // The code is what to fix, so a child who confirmed with the button
      // goes back to the field, once the message has rendered. Otherwise
      // focus stays on the control they pressed, ready for the retry.
      if (codeRefused) focusRefused();
    } finally {
      setVerifying(false);
    }
  };

  const form = busyForm({ busy: verifying, onSubmit: handleVerify });

  // A linked code swaps the form for the "Linked!" line, taking the control the
  // child was on with it. When they were in the form and focus has nowhere
  // better to be, it lands on that line, in the same commit.
  const linkedFocus = useGateFocus(verified ? LINKED_ID : null);

  return (
    <Modal
      overlayClassName="coppa-overlay"
      className="coppa-modal coppa-modal--encourage coppa-modal--wide"
      labelledBy="coppa-pc-title"
      describedBy="coppa-pc-desc"
      onClose={() => onClose?.()}
      busy={verifying}
      initialFocusRef={headingRef}
    >
      <div className="coppa-modal__icon" aria-hidden="true">🌱</div>
      <h2
        id="coppa-pc-title"
        className="coppa-modal__title"
        tabIndex={-1}
        ref={headingRef}
      >
        Let’s ask a grown-up first
      </h2>
      <p id="coppa-pc-desc" className="coppa-modal__desc">
        This part chats with our learning helper, so a parent or guardian
        needs to switch it on for you. You can keep exploring everything else
        while they do.
      </p>

      <ol className="coppa-steps">
        <li>
          <span className="coppa-steps__num" aria-hidden="true">1</span>
          <div className="coppa-steps__body">
            <strong>Ask your parent or guardian</strong> to open Liang on
            their own account and link to you on the{' '}
            {/* The one control here that leaves the overlay by navigating. While
                a code is being checked it is refused like every other way out,
                and stays focusable (aria-disabled, never removed from the Tab
                order): the router follows no link whose click was cancelled. */}
            <Link
              to="/advisor/access"
              className="coppa-inline-link"
              {...busyButton({ busy: verifying, onClick: () => onClose?.() })}
            >
              Guardian access
            </Link>{' '}
            page. They’ll get a one-time code to share with you.
          </div>
        </li>
        <li>
          <span className="coppa-steps__num" aria-hidden="true">2</span>
          <div className="coppa-steps__body">
            <strong>Enter their code below</strong> to confirm the link. Then
            they can switch on the helper from their device — and you’re good
            to go.
          </div>
        </li>
      </ol>

      {verified ? (
        <div
          id={LINKED_ID}
          tabIndex={-1}
          className="coppa-success"
          role="status"
          aria-live="polite"
        >
          Linked! Now ask your grown-up to switch on the learning helper from
          their account, and you’ll be all set.
        </div>
      ) : (
        <form
          {...form.formProps}
          {...linkedFocus.contentProps}
          className="coppa-form"
          noValidate
        >
          <div className="coppa-field">
            <label htmlFor="coppa-pc-token" className="coppa-field__label">
              One-time code from your grown-up
            </label>
            <input
              id="coppa-pc-token"
              ref={tokenRef}
              type="text"
              className="coppa-field__input"
              value={token}
              onChange={(e) => {
                setToken(e.target.value);
                setVerifyError(null);
              }}
              placeholder="Paste the code here"
              autoComplete="off"
              {...form.fieldProps}
              aria-invalid={verifyError?.codeRefused ? 'true' : undefined}
              aria-describedby={verifyError ? TOKEN_ERROR_ID : undefined}
            />
            {verifyError && (
              <p id={TOKEN_ERROR_ID} className="coppa-field__error" role="alert">
                {verifyError.message}
              </p>
            )}
          </div>
          <div className="coppa-modal__actions">
            <Button variant="encourage" {...form.submitProps}>
              {verifying ? 'Confirming…' : 'Confirm the link'}
            </Button>
          </div>
        </form>
      )}
      {/* "Confirming…" lands on a button that is unavailable by then, which
          screen readers don't reliably speak; this live region carries the
          pending cue. Outside the form, so it is there before the check starts
          and outlives the form when the code is linked. */}
      <PendingStatus pending={verifying}>Confirming…</PendingStatus>

      <div className="coppa-modal__footer">
        {/* Natively disabled for the wait: a check starts from the field or
            from Confirm, and that is where focus is. (Except in a browser
            where a pointer press moves no focus: see busyForm.js.) */}
        <Button
          variant="ghost"
          onClick={() => onClose?.()}
          disabled={verifying}
        >
          Keep exploring
        </Button>
      </div>
    </Modal>
  );
}
